import React, { useEffect, useRef, useState, useMemo } from 'react';
import BaseTable, { Column } from 'react-base-table';
import { FLOW_METRICS_DISPLAY } from '@utils/flow/meta';
import useFlowOverview from '../../hooks/useFlowOverview';
import { useFlowConfigs } from '@src/hooks/flow/useFlowConfigs';
import { TableWrapper } from './styled';
import useDndUtils from '../hooks/useDndUtils';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableRow } from './SortableRow';
import { TableHeader } from './TableHeader';

export default function FlowTable() {
  const { flows } = useFlowOverview();
  const flowConfigs = useFlowConfigs();
  const [width, setWidth] = useState(800);
  const containerRef = useRef(null);
  const { handleDragEnd } = useDndUtils(flows);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 100, tolerance: 5 },
    }),
  );

  const rows = useMemo(() => {
    // Include all flows, even deleted ones
    return flows.map((flowId) => {
      const config = flowConfigs[flowId];
      return config ? { ...config } : { id: flowId, name: 'Deleted Flow', isDeleted: true };
    });
  }, [flows, flowConfigs]);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      if (entries.length > 0) {
        const containerWidth = entries[0].contentRect.width;
        // Use container width directly (100% width)
        setWidth(containerWidth);
      }
    });
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => observer.disconnect();
  }, []);

  // drag handle is absolute-positioned; name column absorbs it
  const dragHandleWidth = 32;
  // 2 : 1 : 4 ratio across the available width (after drag handle)
  const available = width - dragHandleWidth;
  const unit = useMemo(() => Math.floor(available / 7), [available]);
  const nameColumnWidth = useMemo(() => unit * 2 + dragHandleWidth, [unit]);
  const initColumnWidth = useMemo(() => unit, [unit]);
  const successColumnWidth = useMemo(
    () => width - nameColumnWidth - initColumnWidth,
    [width, nameColumnWidth, initColumnWidth],
  );
  const effectiveNameWidth = nameColumnWidth - dragHandleWidth;

  const rowHeight = 72;
  const headerHeight = 38;
  const height = rows.length * rowHeight + headerHeight;

  const columnWidths = {
    nameColumnWidth: effectiveNameWidth,
    initColumnWidth,
    successColumnWidth,
  };

  return (
    <TableWrapper ref={containerRef} className="flow-table-container">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={flows} strategy={verticalListSortingStrategy}>
          <BaseTable
            data={rows}
            rowKey="id"
            width={width}
            height={height}
            rowHeight={rowHeight}
            headerHeight={headerHeight}
            rowClassName={() => 'sortable-row'}
            rowRenderer={({ rowData }) => (
              <SortableRow flowMeta={rowData} columnWidths={columnWidths} />
            )}
            overscanColumnCount={1}
          >
            <Column
              key="name"
              dataKey="name"
              width={nameColumnWidth}
              align="left"
              frozen="left"
              headerRenderer={() => (
                <TableHeader title="Name" paddingLeft={`${dragHandleWidth}px`} />
              )}
            />
            <Column
              key="init"
              dataKey="init"
              width={initColumnWidth}
              align="left"
              headerRenderer={() => <TableHeader title={FLOW_METRICS_DISPLAY.init.shortName} />}
            />
            <Column
              key="successPct"
              dataKey="successPct"
              width={successColumnWidth}
              align="left"
              headerRenderer={() => (
                <TableHeader title={`${FLOW_METRICS_DISPLAY.success.shortName} %`} />
              )}
            />
          </BaseTable>
        </SortableContext>
      </DndContext>
    </TableWrapper>
  );
}
